import { inject, Service } from '@angular/core';
import { HttpService } from '@data-services/http/http.service';
import { Company } from '@models/companies';

@Service()
export class CompanyDataService {
  private readonly http = inject(HttpService);

  async getCompaniesAsync(projectId: string): Promise<Company[]> {
    return await this.http.getObjectAsync<Company[]>(`projects/${projectId}/companies`);
  }

  async getCompanyAsync(projectId: string, companyId: string): Promise<Company | undefined> {
    return await this.http.getObjectAsync<Company | undefined>(`projects/${projectId}/companies/${companyId}`);
  }

  async createCompanyAsync(projectId: string, company: Company): Promise<Company> {
    const body: Company = { ...company, Users: [] };
    return await this.http.postObjectAsync<Company, Company>(`projects/${projectId}/companies`, body);
  }

  async updateCompanyAsync(projectId: string, company: Company): Promise<Company> {
    const body = this.applyProjectLeaderRule(company);
    return await this.http.putObjectAsync<Company, Company>(`projects/${projectId}/companies`, body);
  }

  async deleteCompanyUserAsync(companyId: string, mbeUserId: string): Promise<Company> {
    return await this.http.deleteAsync<Company>(`companies/${companyId}/users/${mbeUserId}`);
  }

  async getCompanyDoorCountAsync(companyId: string): Promise<number> {
    return await this.http.getObjectAsync<number>(`companies/${companyId}/doors/count`);
  }

  async deleteCompanyAsync(projectId: string, companyId: string): Promise<void> {
    await this.http.deleteAsync<void>(`projects/${projectId}/companies/${companyId}`);
  }

  /**
   * Legacy rule: when the company's trade template is 'T10' every user is a
   * project leader; otherwise no user is.
   */
  private applyProjectLeaderRule(company: Company): Company {
    const isProjectLeader = company.TradeTemplate?.PropertyName === 'T10';
    const users = (company.Users ?? []).map(user => ({ ...user, IsProjectLeader: isProjectLeader }));
    return { ...company, Users: users };
  }
}
